import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowUp, Loader2, Maximize2, Minimize2, Plus, Sparkles, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BeeGlyph } from '@/components/brand/mm-logo';
import { AnswerPanel } from '@/components/layout/ask-bar';
import { qbeeSuggestions } from '@/lib/qbee-suggestions';
import { useQbee } from './qbee-context';

/**
 * THE QBEE DOCK: the assistant's panel on the right of every page (owner,
 * 11 Oct 2026: "a right side dock style similar to Claude side dock, expand &
 * collapsible ... with few relevant question for that section as suggestions,
 * or take user input text").
 *
 * Beside the page on a wide screen (the page narrows, nothing is covered),
 * over it on a narrow one. It holds one conversation: each question, then the
 * answer as the Ask panel draws it (figures from the reports, explanations
 * from the guides, links to both), with a thumbs up or down. Closed, it leaves
 * a slim tab on the right edge.
 *
 * Shaped like Claude's side panel (owner, 11 Oct 2026: "enlargable by dragging
 * slider, a small icon on the right top to expand and collapse ... rounded
 * corners same as claude"): a card of its own, rounded on every corner, in a
 * gap beside the page; the gap holds the drag handle (double-click puts the
 * width back; arrow keys move it), and the top right has expand / collapse
 * and close.
 */

const WIDTH_KEY = 'qbee.width';
const WIDE_KEY = 'qbee.expanded';
const MIN_W = 340; const MAX_W = 820; const DEFAULT_W = 400;
// Never so wide that the page beside it stops being usable: the sidebar and
// about 640px of page always stay (at 1600px wide the dock reaches 720).
const maxFor = () => (typeof window === 'undefined' ? MAX_W : Math.max(MIN_W, Math.min(MAX_W, window.innerWidth - 880)));
const clampW = (w) => Math.min(maxFor(), Math.max(MIN_W, Math.round(w)));
const wideFor = () => (typeof window === 'undefined' ? 640 : clampW(window.innerWidth * 0.45));

function Thinking() {
  return (
    <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Reading your reports and the guides…
    </p>
  );
}

function Turn({ t, onFollowUp, onRate, onNavigate }) {
  const d = t.data;
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <p className="max-w-[88%] rounded-2xl rounded-br-md bg-primary/10 px-3.5 py-2 text-[14px] leading-relaxed text-foreground">{t.question}</p>
      </div>
      {t.status === 'pending' && <Thinking />}
      {t.status === 'error' && (
        <p className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[13px] text-amber-900 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />{t.error}
        </p>
      )}
      {t.status === 'done' && d && (d.configured === false || d.disabled) && (
        <p className="rounded-lg bg-muted/60 p-3 text-[13px] text-muted-foreground">{d.disabled ? d.answer : (d.needs || 'Qbee is not set up yet.')}</p>
      )}
      {t.status === 'done' && d && d.configured !== false && !d.disabled && (
        <div className="space-y-2">
          <AnswerPanel r={d} compact onFollowUp={onFollowUp} onNavigate={onNavigate} />
          {d.id && (
            <div className="flex items-center gap-1 pt-1 text-muted-foreground">
              <span className="mr-1 text-[12px]">Helpful?</span>
              {[[1, ThumbsUp, 'Yes'], [-1, ThumbsDown, 'No']].map(([v, Icon, label]) => (
                <button key={v} type="button" aria-label={label} onClick={() => onRate(t.id, t.rating === v ? 0 : v)}
                  className={cn('grid size-7 place-items-center rounded-md hover:bg-muted', t.rating === v && 'bg-muted text-foreground')}>
                  <Icon className="size-3.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function QbeeDock() {
  const q = useQbee();
  const location = useLocation();
  const [text, setText] = useState('');
  const [width, setWidth] = useState(() => {
    try { const w = Number(localStorage.getItem(WIDTH_KEY)); return w >= MIN_W && w <= MAX_W ? w : DEFAULT_W; } catch { return DEFAULT_W; }
  });
  const [wide, setWide] = useState(() => { try { return localStorage.getItem(WIDE_KEY) === '1'; } catch { return false; } });
  const [dragging, setDragging] = useState(false);
  const shown = wide ? Math.max(width, wideFor()) : clampW(width);
  // Expand only where the window has room to: at 1280px the dock is already
  // as wide as the page beside it allows.
  const canWiden = wide || wideFor() > clampW(width) + 24;
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const busy = q.turns.some((t) => t.status === 'pending');

  useEffect(() => { try { localStorage.setItem(WIDTH_KEY, String(width)); } catch { /* private window */ } }, [width]);
  useEffect(() => { try { localStorage.setItem(WIDE_KEY, wide ? '1' : '0'); } catch { /* private window */ } }, [wide]);
  // The page card rounds its top-right corner while the dock sits beside it
  // (app-shell reads this on <html>), so the two read as two cards.
  useEffect(() => {
    const el = document.documentElement;
    el.dataset.qbee = q.open ? 'open' : 'closed';
    return () => { delete el.dataset.qbee; };
  }, [q.open]);
  // The newest turn in view.
  useEffect(() => { const el = bodyRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }, [q.turns]);
  useEffect(() => { if (q.open) setTimeout(() => inputRef.current?.focus(), 80); }, [q.open]);

  const send = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (t.length < 3 || busy) return;
    setText('');
    q.ask(t);
  };
  // Drag the handle in the gap to resize; dragging leaves the expanded size.
  const startDrag = (e) => {
    e.preventDefault();
    const x0 = e.clientX; const w0 = shown;
    setDragging(true); setWide(false);
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    const move = (ev) => setWidth(clampW(w0 + (x0 - ev.clientX)));
    const up = () => {
      setDragging(false);
      document.body.style.cursor = prevCursor;
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };
  const keyResize = (e) => {
    const step = e.shiftKey ? 80 : 24;
    if (e.key === 'ArrowLeft') { e.preventDefault(); setWide(false); setWidth(clampW(shown + step)); }
    if (e.key === 'ArrowRight') { e.preventDefault(); setWide(false); setWidth(clampW(shown - step)); }
  };
  const resetWidth = () => { setWide(false); setWidth(DEFAULT_W); };

  if (!q.open) {
    return (
      <button type="button" onClick={q.openDock} aria-label="Ask Qbee" title="Ask Qbee about this page"
        className="fixed right-0 top-1/2 z-30 hidden -translate-y-1/2 items-center gap-1.5 rounded-l-xl border border-r-0 border-border bg-card px-1.5 py-3 text-[12px] font-medium text-muted-foreground shadow-sm hover:text-foreground lg:flex print:hidden"
        style={{ writingMode: 'vertical-rl' }}>
        <BeeGlyph size={16} animate={false} /> Ask Qbee
      </button>
    );
  }

  const suggestions = qbeeSuggestions(location.pathname, location.search);
  const where = q.page.title || 'this page';

  return (
    <>
      {/* Narrow screens: over the page, with a shade to close it. */}
      <div className="fixed inset-0 z-40 bg-black/30 lg:hidden print:hidden" onClick={q.closeDock} aria-hidden="true" />
      {/* The card's frame: on a wide screen a column beside the page whose
          left padding is the gap the drag handle lives in. */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[440px] sm:inset-y-2 sm:right-2 lg:relative lg:inset-auto lg:z-auto lg:w-auto lg:max-w-none lg:shrink-0 lg:py-2 lg:pl-2.5 lg:pr-2 print:hidden">
        <div role="separator" aria-orientation="vertical" aria-label="Resize Qbee" aria-valuenow={shown} aria-valuemin={MIN_W} aria-valuemax={MAX_W}
          tabIndex={0} title="Drag to resize · double-click to reset" onMouseDown={startDrag} onDoubleClick={resetWidth} onKeyDown={keyResize}
          className="group absolute inset-y-0 left-0 hidden w-2.5 cursor-col-resize items-center justify-center outline-none lg:flex">
          <span className={cn('h-12 w-1 rounded-full bg-border transition-colors group-hover:bg-primary/50 group-focus-visible:bg-primary', dragging && 'bg-primary')} />
        </div>
        <aside aria-label="Qbee" style={{ '--qbee-w': `${shown}px` }}
          className={cn('flex w-full flex-col overflow-hidden bg-card shadow-2xl sm:rounded-2xl sm:border sm:border-border/80',
            'lg:w-[var(--qbee-w)] lg:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)]',
            !dragging && 'lg:transition-[width] lg:duration-200 lg:ease-out')}>
          <div className="flex items-center gap-2.5 border-b border-border/70 py-2.5 pl-4 pr-2">
            <BeeGlyph size={22} />
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold leading-tight">Qbee</p>
              <p className="truncate text-[12px] text-muted-foreground">On {where}</p>
            </div>
            <button type="button" onClick={q.newConversation} title="New conversation" aria-label="New conversation"
              className="grid size-8 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"><Plus className="size-4" /></button>
            <button type="button" onClick={() => setWide((v) => !v)} title={wide ? 'Collapse' : 'Expand'} aria-label={wide ? 'Collapse Qbee' : 'Expand Qbee'} aria-pressed={wide}
              className={cn('hidden size-8 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground', canWiden && 'lg:grid')}>
              {wide ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </button>
            <button type="button" onClick={q.closeDock} title="Close" aria-label="Close Qbee"
              className="grid size-8 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"><X className="size-4" /></button>
          </div>

        <div ref={bodyRef} className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
          {!q.turns.length && (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-[15px] font-medium">Ask about {where}</p>
                <p className="text-[13px] leading-relaxed text-muted-foreground">
                  Your numbers, what something on the page means, or how to set something up. Qbee reads your reports and the Cookiebees guides, and says where each answer came from.
                </p>
              </div>
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => q.ask(s)}
                    className="flex w-full items-start gap-2 rounded-lg border border-[hsl(var(--card-border))] bg-background px-3 py-2.5 text-left text-[13.5px] leading-snug transition-colors hover:border-primary/50 hover:bg-primary/[0.04]">
                    <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />{s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {q.turns.map((t) => (
            <Turn key={t.id} t={t} onFollowUp={(f) => q.ask(f)} onRate={q.rate}
              onNavigate={() => { if (typeof window !== 'undefined' && window.innerWidth < 1024) q.closeDock(); }} />
          ))}
        </div>

        <form onSubmit={send} className="px-3 pb-3 pt-1.5">
          <div className="flex items-end gap-2 rounded-2xl border border-border bg-background px-3.5 py-2.5 shadow-[0_1px_3px_rgba(0,0,0,0.05)] focus-within:border-border-strong focus-within:ring-2 focus-within:ring-ring/30">
            <textarea ref={inputRef} rows={1} value={text} onChange={(e) => setText(e.target.value)} maxLength={500}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
              placeholder="Ask Qbee…" aria-label="Ask Qbee"
              className="max-h-32 min-h-[24px] flex-1 resize-none bg-transparent text-[16px] leading-6 outline-none placeholder:text-muted-foreground lg:text-[14px]" />
            <button type="submit" disabled={busy || text.trim().length < 3} aria-label="Send"
              className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground transition-opacity disabled:opacity-40">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
            </button>
          </div>
          <p className="px-1 pt-1.5 text-[11.5px] text-muted-foreground">Qbee reads; it never changes a setting. Check anything important on the report it links.</p>
        </form>
        </aside>
      </div>
    </>
  );
}

export default QbeeDock;
